import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import SEOHead from '../../components/SEOHead';

export default function HowWeWorkPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const howWeWorkStructuredData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How Technelle Delivers AI Marketing Results for Wisconsin Businesses",
    "description": "A proven 3-phase marketing framework — Strategy, Execution, and Optimization — refined over 20 years of driving growth for Wisconsin businesses.",
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Discovery & Strategy",
        "text": "We start by deeply understanding your Wisconsin business, market, and goals. We conduct a business audit, Wisconsin market and competitive analysis, AI opportunity assessment, and develop a strategic roadmap."
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Implementation & Build",
        "text": "We execute the strategy with precision, launching marketing campaigns, building custom AI tools, integrating systems and automation, and creating and deploying content."
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Measure & Optimize",
        "text": "We continuously track performance, analyze results, and optimize for better outcomes through performance tracking, A/B testing, AI model refinement, and continuous improvement cycles."
      }
    ],
    "totalTime": "P90D",
    "tool": [
      { "@type": "HowToTool", "name": "AI Marketing Automation" },
      { "@type": "HowToTool", "name": "SEO & Content Strategy" },
      { "@type": "HowToTool", "name": "Analytics & Reporting Dashboard" }
    ]
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead 
        title="Our Process | Wisconsin Marketing & AI Consulting | Technelle"
        description="Discover Technelle's proven 3-phase marketing framework for Wisconsin businesses. From strategy development to execution and optimization, learn how we help Waukesha and Wisconsin companies achieve measurable growth through AI-powered marketing."
        keywords="marketing process Wisconsin, AI consulting process, marketing framework, Wisconsin business consulting, strategic marketing process, AI implementation process, marketing methodology Wisconsin"
        canonical="https://technelle.ai/how-we-work"
        ogTitle="Our Process | Wisconsin Marketing & AI Consulting | Technelle"
        ogDescription="Our proven 3-phase framework for delivering measurable marketing results to Wisconsin businesses."
        ogUrl="https://technelle.ai/how-we-work"
        structuredData={howWeWorkStructuredData}
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'How We Work', url: 'https://technelle.ai/how-we-work' }
        ]}
      />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4">
            <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle Logo" className="h-10" />
          </Link>
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/#about" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">About</Link>
            <Link to="/services" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Services</Link>
            <Link to="/#case-studies" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Case Studies</Link>
            <Link to="/resources" className="text-[16px] text-[#1a1a1a] hover:text-[#14B8A6] transition-colors font-medium">Resources</Link>
            <Link to="/how-we-work" className="text-[16px] text-[#14B8A6] font-medium transition-colors">How We Work</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/#contact" className="hidden lg:block bg-[#14B8A6] text-white px-6 py-2.5 rounded-full text-[16px] font-semibold hover:bg-[#0F9688] transition-colors whitespace-nowrap shadow-lg">
              Book Free Strategy Call
            </Link>
            <button
              onClick={() => setMobileNavOpen(true)}
              className="lg:hidden w-11 h-11 flex items-center justify-center text-[#1a1a1a] cursor-pointer"
              aria-label="Open mobile menu"
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Nav Drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 bg-black/60 z-[200] lg:hidden" onClick={() => setMobileNavOpen(false)}>
          <div className="absolute top-0 right-0 w-72 h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 h-20 border-b border-gray-100">
              <span className="text-[#1a1a1a] font-semibold text-[16px]">Menu</span>
              <button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 flex items-center justify-center text-[#1a1a1a] cursor-pointer" aria-label="Close menu">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <nav className="flex flex-col px-6 py-8 gap-6">
              <Link to="/" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Home</Link>
              <Link to="/about" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">About</Link>
              <Link to="/services" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Services</Link>
              <Link to="/resources" onClick={() => setMobileNavOpen(false)} className="text-[#1a1a1a] hover:text-[#14B8A6] text-[16px] font-medium transition-colors">Resources</Link>
              <Link to="/how-we-work" onClick={() => setMobileNavOpen(false)} className="text-[#14B8A6] text-[16px] font-medium">How We Work</Link>
            </nav>
            <div className="px-6 mt-auto pb-8">
              <Link
                to="/#contact"
                onClick={() => setMobileNavOpen(false)}
                className="block w-full text-center bg-[#14B8A6] text-white py-4 rounded-full text-[15px] font-semibold hover:bg-[#0F9688] transition-colors"
              >
                Book Free Strategy Call
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-pink-50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/72a638c58ad26529d995e00fd1570e6a.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="1000"
            height="1200"
          />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center pt-20">
          <span className="text-[14px] uppercase tracking-wider text-[#2d5016] font-semibold">Our Approach</span>
          <h1 className="text-[36px] sm:text-[48px] lg:text-[64px] leading-[1.1] font-serif font-bold text-[#1a1a1a] mt-6 mb-8">
            Strategy → Execution → Optimization
          </h1>
          <p className="text-[18px] sm:text-[22px] text-[#666666] max-w-3xl mx-auto leading-relaxed">
            A proven framework refined over 18 years of driving growth for Wisconsin businesses across industries.
          </p>
        </div>
      </section>

      {/* Process Overview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Phase 1 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-2xl transition-all relative">
              <div className="text-[80px] font-serif font-bold text-[#f5f3ef] absolute top-8 left-8">01</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#2d5016] rounded-xl flex items-center justify-center mb-8 mt-16">
                  <i className="ri-lightbulb-line text-white text-3xl"></i>
                </div>
                <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a] mb-6">Discovery & Strategy</h3>
                <p className="text-[16px] text-[#666666] mb-8 leading-relaxed">
                  We start by deeply understanding your Wisconsin business, market, and goals. Then we develop a comprehensive strategy tailored to your unique situation.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Business audit and goal setting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Wisconsin market and competitive analysis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">AI opportunity assessment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Strategic roadmap development</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Phase 2 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-2xl transition-all relative">
              <div className="text-[80px] font-serif font-bold text-[#f5f3ef] absolute top-8 left-8">02</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#14B8A6] rounded-xl flex items-center justify-center mb-8 mt-16">
                  <i className="ri-tools-line text-white text-3xl"></i>
                </div>
                <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a] mb-6">Implementation & Build</h3>
                <p className="text-[16px] text-[#666666] mb-8 leading-relaxed">
                  We execute the strategy with precision, building campaigns, tools, and systems designed to drive measurable results for your Wisconsin business.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Marketing campaign launches</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Custom AI tool development</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">System integration and automation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#14B8A6] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Content creation and deployment</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Phase 3 */}
            <div className="bg-white rounded-2xl p-12 shadow-lg hover:shadow-2xl transition-all relative">
              <div className="text-[80px] font-serif font-bold text-[#f5f3ef] absolute top-8 left-8">03</div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#2d5016] rounded-xl flex items-center justify-center mb-8 mt-16">
                  <i className="ri-line-chart-line text-white text-3xl"></i>
                </div>
                <h3 className="text-[28px] font-serif font-bold text-[#1a1a1a] mb-6">Measure & Optimize</h3>
                <p className="text-[16px] text-[#666666] mb-8 leading-relaxed">
                  We continuously track performance, analyze results, and optimize for better outcomes. Growth is an ongoing process for Wisconsin clients.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Performance tracking and reporting</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">A/B testing and experimentation</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">AI model refinement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-[#2d5016] text-xl mt-1"></i>
                    <span className="text-[15px] text-[#1a1a1a]">Continuous improvement cycles</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Connection Arrows */}
          <div className="flex items-center justify-center gap-8 mt-12">
            <div className="flex items-center gap-4">
              <div className="w-24 h-1 bg-[#d0d0d0]"></div>
              <i className="ri-arrow-right-line text-[#14B8A6] text-3xl"></i>
              <div className="w-24 h-1 bg-[#d0d0d0]"></div>
              <i className="ri-arrow-right-line text-[#14B8A6] text-3xl"></i>
              <div className="w-24 h-1 bg-[#d0d0d0]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-24 bg-[#faf8f5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-6">What Makes Us Different</h2>
            <p className="text-[18px] text-[#666666] max-w-2xl mx-auto leading-relaxed">
              We're not your typical agency. Here's what sets us apart when working with Wisconsin businesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-10">
              <div className="w-14 h-14 bg-[#2d5016] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-user-star-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Expert-Led, Not Junior-Staffed</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                You work directly with senior strategists who have 18+ years of experience serving Wisconsin businesses. No junior account managers, no handoffs to inexperienced teams.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10">
              <div className="w-14 h-14 bg-[#2d5016] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-focus-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Results-Focused, Not Activity-Focused</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                We measure success by business outcomes — revenue, leads, efficiency — not vanity metrics like impressions or followers.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10">
              <div className="w-14 h-14 bg-[#14B8A6] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-robot-2-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Practical AI, Not Hype</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                We implement AI solutions that deliver measurable ROI for Wisconsin businesses, not experimental technology. Every AI tool we build solves a real business problem.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10">
              <div className="w-14 h-14 bg-[#2d5016] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Partners, Not Vendors</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                We're invested in your long-term success. We challenge assumptions, provide honest feedback, and act as an extension of your Wisconsin team.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10">
              <div className="w-14 h-14 bg-[#2d5016] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-eye-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Transparent Process</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                You'll always know what we're working on, why we're doing it, and what results we're seeing. No black boxes, no surprises.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10">
              <div className="w-14 h-14 bg-[#2d5016] rounded-lg flex items-center justify-center mb-6">
                <i className="ri-stack-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[24px] font-serif font-bold text-[#1a1a1a] mb-4">Integrated Approach</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                Marketing and AI work together seamlessly. We don't silo services — we build comprehensive growth systems for Wisconsin businesses.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-6">What to Expect: First 90 Days</h2>
            <p className="text-[18px] text-[#666666] max-w-2xl mx-auto leading-relaxed">
              Here's a typical timeline for new Wisconsin partnerships. Every engagement is customized, but this gives you a sense of our process.
            </p>
          </div>

          <div className="space-y-8">
            {/* Week 1-2 */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 items-start">
              <div className="flex-shrink-0 sm:w-32 sm:text-right">
                <div className="text-[20px] sm:text-[24px] font-serif font-bold text-[#14B8A6]">Week 1-2</div>
              </div>
              <div className="flex-1 bg-[#faf8f5] rounded-2xl p-6 sm:p-8">
                <h3 className="text-[22px] font-semibold text-[#1a1a1a] mb-3">Discovery & Onboarding</h3>
                <p className="text-[16px] text-[#666666] leading-relaxed">
                  Deep dive into your Wisconsin business, market, customers, and goals. We audit existing marketing and operations to identify opportunities.
                </p>
              </div>
            </div>

            {/* Week 3-4 */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 items-start">
              <div className="flex-shrink-0 sm:w-32 sm:text-right">
                <div className="text-[20px] sm:text-[24px] font-serif font-bold text-[#14B8A6]">Week 3-4</div>
              </div>
              <div className="flex-1 bg-[#faf8f5] rounded-2xl p-6 sm:p-8">
                <h3 className="text-[22px] font-semibold text-[#1a1a1a] mb-3">Strategy Development</h3>
                <p className="text-[16px] text-[#666666] leading-relaxed">
                  Present comprehensive strategy including marketing roadmap, AI implementation plan, and success metrics tailored to your Wisconsin business. Refine based on your feedback.
                </p>
              </div>
            </div>

            {/* Week 5-8 */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 items-start">
              <div className="flex-shrink-0 sm:w-32 sm:text-right">
                <div className="text-[20px] sm:text-[24px] font-serif font-bold text-[#14B8A6]">Week 5-8</div>
              </div>
              <div className="flex-1 bg-[#faf8f5] rounded-2xl p-6 sm:p-8">
                <h3 className="text-[22px] font-semibold text-[#1a1a1a] mb-3">Initial Implementation</h3>
                <p className="text-[16px] text-[#666666] leading-relaxed">
                  Launch first campaigns, begin building AI tools, set up tracking and analytics. Focus on quick wins while building long-term systems.
                </p>
              </div>
            </div>

            {/* Week 9-12 */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 items-start">
              <div className="flex-shrink-0 sm:w-32 sm:text-right">
                <div className="text-[20px] sm:text-[24px] font-serif font-bold text-[#14B8A6]">Week 9-12</div>
              </div>
              <div className="flex-1 bg-[#faf8f5] rounded-2xl p-6 sm:p-8">
                <h3 className="text-[22px] font-semibold text-[#1a1a1a] mb-3">Optimization & Scaling</h3>
                <p className="text-[16px] text-[#666666] leading-relaxed">
                  Analyze early results, optimize based on data, scale what's working. Present 90-day review and roadmap for next quarter.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Communication & Reporting */}
      <section className="py-24 bg-[#faf8f5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-16">
            <h2 className="text-[48px] font-serif font-bold text-[#1a1a1a] mb-6">Communication & Reporting</h2>
            <p className="text-[18px] text-[#666666] max-w-2xl mx-auto leading-relaxed">
              Stay informed every step of the way with regular updates and transparent reporting for your Wisconsin business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-10 text-center">
              <div className="w-16 h-16 bg-[#2d5016] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-check-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[22px] font-serif font-bold text-[#1a1a1a] mb-4">Weekly Check-ins</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                Quick 30-minute calls to review progress, address questions, and align on priorities.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 text-center">
              <div className="w-16 h-16 bg-[#14B8A6] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-chart-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[22px] font-serif font-bold text-[#1a1a1a] mb-4">Monthly Reports</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                Comprehensive performance reports with insights, analysis, and recommendations for the month ahead.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-10 text-center">
              <div className="w-16 h-16 bg-[#2d5016] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-dashboard-3-line text-white text-2xl"></i>
              </div>
              <h3 className="text-[22px] font-serif font-bold text-[#1a1a1a] mb-4">Real-Time Dashboard</h3>
              <p className="text-[16px] text-[#666666] leading-relaxed">
                Access your custom dashboard anytime to see current performance metrics and campaign status.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Start Here CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="bg-[#1a1a1a] rounded-3xl p-8 sm:p-16 text-center">
            <h2 className="text-[36px] sm:text-[52px] font-serif font-bold text-white mb-6 leading-tight">Overwhelmed? Start Here.</h2>
            <p className="text-[20px] text-[#cccccc] mb-12 max-w-2xl mx-auto leading-relaxed">
              Not sure where to begin? Book a free 30-minute strategy call. We'll help you identify your biggest opportunities and create a clear path forward for your Wisconsin business.
            </p>
            
            <div className="space-y-6">
              <Link to="/#contact" className="bg-[#14B8A6] text-white px-12 py-5 rounded-lg text-[18px] font-medium hover:bg-[#0F9688] transition-all inline-block whitespace-nowrap">
                Book Your Free Strategy Call
              </Link>
              
              <div className="text-[#999999] text-[15px]">
                No pressure, no sales pitch — just honest advice from an experienced Wisconsin marketing consultant.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mt-16 pt-12 border-t border-white/10">
              <div>
                <div className="text-[32px] font-serif font-bold text-white mb-2">30 min</div>
                <div className="text-[#999999] text-[15px]">Free consultation</div>
              </div>
              <div>
                <div className="text-[32px] font-serif font-bold text-white mb-2">Zero</div>
                <div className="text-[#999999] text-[15px]">Obligation to hire us</div>
              </div>
              <div>
                <div className="text-[32px] font-serif font-bold text-white mb-2">100%</div>
                <div className="text-[#999999] text-[15px]">Actionable insights</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white rounded-t-[32px] py-20 px-4 sm:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-16">
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-center gap-4 mb-6">
                <img src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82" alt="Technelle Logo" className="h-5" loading="lazy" decoding="async" width="80" height="20" />
                <span className="text-[20px] font-bold text-white">Technelle</span>
              </div>
              <p className="text-[16px] text-[#999999]">AI-Driven Marketing & Operational Excellence</p>
            </div>

            <div>
              <div className="text-[14px] uppercase tracking-wider text-[#999999] mb-6 font-semibold">Quick Links</div>
              <nav className="space-y-4">
                <Link to="/#about" className="block text-white hover:text-[#14B8A6] transition-colors">About Us</Link>
                <Link to="/services" className="block text-white hover:text-[#14B8A6] transition-colors">Services</Link>
                <Link to="/#case-studies" className="block text-white hover:text-[#14B8A6] transition-colors">Case Studies</Link>
                <Link to="/resources" className="block text-white hover:text-[#14B8A6] transition-colors">Resources</Link>
                <Link to="/how-we-work" className="block text-white hover:text-[#14B8A6] transition-colors">How We Work</Link>
              </nav>
            </div>

            <div>
              <div className="text-[14px] uppercase tracking-wider text-[#999999] mb-6 font-semibold">Get Started</div>
              <div className="text-[24px] text-white mb-2">
                <a href="tel:4145309114" className="hover:text-[#14B8A6] transition-colors">414-530-9114</a>
              </div>
              <div className="text-[16px] text-[#999999]">
                <a href="mailto:melissa@technelle.ai" className="hover:text-[#14B8A6] transition-colors">melissa@technelle.ai</a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-8">
                <a href="https://linkedin.com/company/technelle" aria-label="Follow Technelle on LinkedIn" className="text-[#999999] hover:text-[#14B8A6] transition-colors">
                  <i className="ri-linkedin-fill text-2xl"></i>
                </a>
                <a href="https://twitter.com/technelle" aria-label="Follow Technelle on Twitter" className="text-[#999999] hover:text-[#14B8A6] transition-colors">
                  <i className="ri-twitter-x-fill text-2xl"></i>
                </a>
              </div>
              <div className="text-[14px] text-[#999999]">© 2025 Technelle. All rights reserved.</div>
              <div className="flex items-center gap-4 flex-wrap justify-center">
                <Link to="/privacy-policy" className="text-[14px] text-[#999999] hover:text-white transition-colors">Privacy Policy</Link>
                <span className="text-[#333333]">|</span>
                <Link to="/terms" className="text-[14px] text-[#999999] hover:text-white transition-colors">Terms</Link>
                <span className="text-[#333333]">|</span>

              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}